import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  YBtn, YIconBtn, YField, YInput, YSelect, YBadge, YAvatar, YTabs,
  colorFor, initialsOf,
} from '../../components/yool/YoolPrimitives';
import { YIcon } from '../../components/yool/YoolIcons';
import {
  getPersonas,
  getPersonaById,
  getCertificadosByPersonaId,
  createPersona,
  updatePersona,
} from '../../services/supabase/personasService';
import { useSupabaseClient } from '../../lib/supabase';

const SEXOS = ['Masculino', 'Femenino', 'Otro'];
const ORIGENES = ['web', 'empresa', 'manual', 'otro'];

const EMPTY_PERSONA = {
  rut: '', nombre: '', apellido_paterno: '', apellido_materno: '',
  fecha_nacimiento: '', sexo: '', nacionalidad: '',
  email: '', telefono: '',
  domicilio: '', numero: '', oficina: '', comuna: '', region: '',
  prevision: '', profesion: '',
  banco: '', tipo_cuenta: '', numero_cuenta: '',
  origen: '',
};

function nombreCompleto(p) {
  return [p?.nombre, p?.apellido_paterno, p?.apellido_materno].filter(Boolean).join(' ');
}

function PersonaForm({ persona, onSave, saving, dirty, setDirty }) {
  const [form, setForm] = useState(persona || EMPTY_PERSONA);

  useEffect(() => {
    setForm(persona || EMPTY_PERSONA);
    setDirty(false);
  }, [persona?.id]);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setDirty(true); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.rut.trim()) { toast.error('El RUT es requerido'); return; }
    if (!form.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    if (!form.apellido_paterno.trim()) { toast.error('El apellido paterno es requerido'); return; }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="y-form-body" style={{ paddingBottom: 96 }}>
      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Identidad</div>
        </div>
        <div className="y-grid-2">
          <YField label="RUT" required>
            <YInput value={form.rut} onChange={e => set('rut', e.target.value)} placeholder="12.345.678-9" />
          </YField>
          <YField label="Nacionalidad">
            <YInput value={form.nacionalidad || ''} onChange={e => set('nacionalidad', e.target.value)} placeholder="Chilena" />
          </YField>
          <YField label="Nombre" required>
            <YInput value={form.nombre} onChange={e => set('nombre', e.target.value)} />
          </YField>
          <YField label="Apellido paterno" required>
            <YInput value={form.apellido_paterno} onChange={e => set('apellido_paterno', e.target.value)} />
          </YField>
          <YField label="Apellido materno">
            <YInput value={form.apellido_materno || ''} onChange={e => set('apellido_materno', e.target.value)} />
          </YField>
          <YField label="Fecha de nacimiento">
            <YInput type="date" value={form.fecha_nacimiento || ''} onChange={e => set('fecha_nacimiento', e.target.value)} />
          </YField>
          <YField label="Sexo">
            <YSelect value={form.sexo || ''} onChange={e => set('sexo', e.target.value)}>
              <option value="">Seleccionar…</option>
              {SEXOS.map(s => <option key={s} value={s}>{s}</option>)}
            </YSelect>
          </YField>
          <YField label="Origen">
            <YSelect value={form.origen || ''} onChange={e => set('origen', e.target.value)}>
              <option value="">Seleccionar…</option>
              {ORIGENES.map(o => <option key={o} value={o}>{o}</option>)}
            </YSelect>
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Contacto</div>
        </div>
        <div className="y-grid-2">
          <YField label="Email">
            <YInput type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="tu@correo.cl" />
          </YField>
          <YField label="Telefono">
            <YInput value={form.telefono || ''} onChange={e => set('telefono', e.target.value)} placeholder="+56 9 0000 0000" />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Domicilio</div>
        </div>
        <div className="y-grid-4">
          <YField label="Calle" span={2}>
            <YInput value={form.domicilio || ''} onChange={e => set('domicilio', e.target.value)} />
          </YField>
          <YField label="Numero">
            <YInput value={form.numero || ''} onChange={e => set('numero', e.target.value)} />
          </YField>
          <YField label="Oficina / Dpto">
            <YInput value={form.oficina || ''} onChange={e => set('oficina', e.target.value)} />
          </YField>
          <YField label="Comuna" span={2}>
            <YInput value={form.comuna || ''} onChange={e => set('comuna', e.target.value)} />
          </YField>
          <YField label="Region" span={2}>
            <YInput value={form.region || ''} onChange={e => set('region', e.target.value)} />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Datos profesionales y prevision</div>
        </div>
        <div className="y-grid-2">
          <YField label="Profesion">
            <YInput value={form.profesion || ''} onChange={e => set('profesion', e.target.value)} />
          </YField>
          <YField label="Prevision">
            <YInput value={form.prevision || ''} onChange={e => set('prevision', e.target.value)} placeholder="Fonasa, Banmedica, …" />
          </YField>
        </div>
      </div>

      <div className="y-form-section">
        <div className="y-form-section-head">
          <div className="y-form-section-eyebrow">Datos bancarios</div>
        </div>
        <div className="y-grid-3">
          <YField label="Banco">
            <YInput value={form.banco || ''} onChange={e => set('banco', e.target.value)} />
          </YField>
          <YField label="Tipo de cuenta">
            <YInput value={form.tipo_cuenta || ''} onChange={e => set('tipo_cuenta', e.target.value)} />
          </YField>
          <YField label="Numero de cuenta">
            <YInput value={form.numero_cuenta || ''} onChange={e => set('numero_cuenta', e.target.value)} />
          </YField>
        </div>
      </div>
    </form>
  );
}

function PolizasTab({ personaId, supabaseClient }) {
  const [polizas, setPolizas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (personaId) loadPolizas();
  }, [personaId]);

  const loadPolizas = async () => {
    try {
      setLoading(true);
      const data = await getCertificadosByPersonaId(personaId, supabaseClient);
      setPolizas(data);
    } catch {
      toast.error('Error al cargar polizas');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (f) => f
    ? new Date(f).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  if (loading) return (
    <div className="y-form-body">
      <p style={{ fontSize: 13, color: 'var(--yool-muted)' }}>Cargando polizas...</p>
    </div>
  );

  if (polizas.length === 0) {
    return (
      <div className="y-form-body">
        <div className="y-list-empty">
          Este cliente todavia no tiene polizas registradas.
        </div>
      </div>
    );
  }

  return (
    <div className="y-form-body">
      {polizas.map(p => {
        const cert = p.certificados;
        const sub = p.subscriptions;
        const emp = p.empresa_empleadores;
        return (
          <div key={p.id} className="y-row">
            <div>
              <div className="y-row-head">
                {cert?.codigo && <span className="y-row-code">{cert.codigo}</span>}
                {sub?.plan_name && <span className="y-row-title">{sub.plan_name}</span>}
                <YBadge tone={p.estado === 'activo' ? 'success' : 'muted'} dot>
                  {p.estado || 'activo'}
                </YBadge>
              </div>
              <div className="y-row-meta">
                {emp && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <YIcon.Building size={12} />
                    {emp.nombre_fantasia || emp.razon_social}
                  </span>
                )}
                {cert && (
                  <>
                    <span className="y-dot">·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <YIcon.Calendar size={12} />
                      {fmt(cert.fecha_inicio_cobertura)} → {fmt(cert.fecha_termino_cobertura)}
                    </span>
                  </>
                )}
                {p.pagador && (
                  <>
                    <span className="y-dot">·</span>
                    <span>Pagador: {p.pagador}</span>
                  </>
                )}
              </div>
            </div>
            <div className="y-row-right">
              {sub?.amount && (
                <div className="y-row-amount">
                  <span className="y-unit">{sub.currency || 'CLP'}</span>
                  {Number(sub.amount).toLocaleString('es-CL')}
                </div>
              )}
              <YIconBtn variant="quiet" size="sm" title="Ver certificado">
                <YIcon.ExternalLink size={13} />
              </YIconBtn>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClientesList() {
  const supabaseClient = useSupabaseClient();
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { loadPersonas(); }, [supabaseClient]);

  const loadPersonas = async () => {
    try {
      setLoading(true);
      const data = await getPersonas(supabaseClient);
      setPersonas(data);
    } catch {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (persona) => {
    setSelectedPersona(persona);
    setIsCreating(false);
    setTab('info');
    try {
      const full = await getPersonaById(persona.id, supabaseClient);
      setDetalle(full);
    } catch {
      setDetalle(persona);
    }
  };

  const handleSave = async (form) => {
    try {
      setSaving(true);
      if (isCreating) {
        const nueva = await createPersona(form, supabaseClient);
        toast.success('Cliente creado');
        await loadPersonas();
        await handleSelect(nueva);
        setIsCreating(false);
      } else {
        const updated = await updatePersona(selectedPersona.id, form, supabaseClient);
        toast.success('Cliente actualizado');
        setDetalle(updated);
        setSelectedPersona(updated);
        await loadPersonas();
      }
      setDirty(false);
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const personasFiltradas = personas.filter(p => {
    const q = search.toLowerCase();
    return (
      p.rut?.toLowerCase().includes(q) ||
      nombreCompleto(p).toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
    );
  });

  const selected = detalle || selectedPersona;

  return (
    <div className="y-workspace">
      {/* Panel izquierdo */}
      <div className="y-master">
        <div className="y-master-head">
          <div className="y-master-title">
            <YIcon.User size={14} style={{ color: 'var(--yool-muted)', flexShrink: 0 }} />
            <span className="y-fy">
              Clientes <span className="fy-teal">activos</span>
            </span>
            <span className="y-count">{personasFiltradas.length}</span>
          </div>
          <div className="y-master-search">
            <YIcon.Search />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre, RUT o email…"
            />
          </div>
          <div className="y-master-actions">
            <YBtn
              variant="outline"
              size="sm"
              icon={<YIcon.Plus size={13} />}
              style={{ flex: 1 }}
              onClick={() => {
                setIsCreating(true);
                setSelectedPersona(null);
                setDetalle(null);
                setTab('info');
              }}
            >
              Nuevo cliente
            </YBtn>
            <YIconBtn variant="ghost" size="sm" title="Recargar" onClick={loadPersonas}>
              <YIcon.Refresh size={13} />
            </YIconBtn>
          </div>
        </div>

        <div className="y-master-list">
          {loading ? (
            <div className="y-master-empty">Cargando...</div>
          ) : personasFiltradas.length === 0 ? (
            <div className="y-master-empty">Ajustemos los filtros y lo encontramos.</div>
          ) : personasFiltradas.map(p => {
            const display = nombreCompleto(p);
            const active = !isCreating && selectedPersona?.id === p.id;
            return (
              <div
                key={p.id}
                className={`y-list-item${active ? ' active' : ''}`}
                onClick={() => handleSelect(p)}
              >
                <YAvatar
                  initials={initialsOf(display) || '·'}
                  color={colorFor(p.id)}
                  size={32}
                  square={false}
                />
                <div className="y-list-item-body">
                  <div className="y-list-item-name">
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
                  </div>
                  <div className="y-list-item-meta">{p.rut}</div>
                  <div className="y-list-item-tags">
                    {p.origen && <YBadge tone="outline">{p.origen}</YBadge>}
                    {p.tiene_cuenta && <YBadge tone="success" dot>cuenta</YBadge>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Panel derecho */}
      <div className="y-detail">
        {isCreating ? (
          <div className="y-detail-card">
            <div className="y-detail-head">
              <div style={{
                width: 48, height: 48, borderRadius: 9999,
                background: 'var(--yool-navy)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', flexShrink: 0,
              }}>
                <YIcon.User size={22} />
              </div>
              <div>
                <div className="y-detail-head-eyebrow">Persona natural</div>
                <div className="y-detail-head-title">Nuevo cliente</div>
                <div className="y-detail-head-meta">Solo necesitamos RUT, nombre y apellido para empezar.</div>
              </div>
            </div>
            <PersonaForm onSave={handleSave} saving={saving} dirty={dirty} setDirty={setDirty} />
            <div className="y-form-footer">
              <div className="y-form-footer-meta">
                Campos con <span style={{ color: 'var(--yool-cta)', fontWeight: 700 }}>*</span> son obligatorios.
              </div>
              <div className="y-form-footer-actions">
                <YBtn variant="quiet" onClick={() => setIsCreating(false)}>Cancelar</YBtn>
                <YBtn
                  variant="primary"
                  disabled={saving}
                  onClick={() => document.querySelector('.y-detail-card form')?.requestSubmit()}
                >
                  {saving ? 'Guardando...' : 'Crear cliente'}
                </YBtn>
              </div>
            </div>
          </div>
        ) : !selectedPersona ? (
          <div className="y-detail-empty">
            <div className="y-glyph"><YIcon.User size={28} /></div>
            <div>
              <h3>Selecciona un cliente</h3>
              <p>Elige uno de la lista o crea <span className="fy-teal">uno nuevo</span> para gestionar sus datos y polizas.</p>
            </div>
            <YBtn variant="outline" icon={<YIcon.Plus size={13} />} onClick={() => setIsCreating(true)}>
              Nuevo cliente
            </YBtn>
          </div>
        ) : (
          <div className="y-detail-card">
            <div className="y-detail-head">
              <YAvatar
                initials={initialsOf(nombreCompleto(selected))}
                color={colorFor(selectedPersona.id)}
                size={48}
                square={false}
              />
              <div style={{ minWidth: 0 }}>
                <div className="y-detail-head-eyebrow">Cliente · {selected?.rut}</div>
                <div className="y-detail-head-title">
                  {nombreCompleto(selected)}
                  {selected?.origen && <YBadge tone="outline">{selected.origen}</YBadge>}
                  {selected?.tiene_cuenta && <YBadge tone="success" dot>con cuenta</YBadge>}
                </div>
                <div className="y-detail-head-meta">
                  {selected?.email && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <YIcon.Mail size={12} /> {selected.email}
                    </span>
                  )}
                  {selected?.telefono && (
                    <>
                      <span className="y-dot">·</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <YIcon.Phone size={12} /> {selected.telefono}
                      </span>
                    </>
                  )}
                  {selected?.comuna && (
                    <>
                      <span className="y-dot">·</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <YIcon.Pin size={12} /> {selected.comuna}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="y-detail-head-actions">
                <YIconBtn variant="quiet" title="Mas opciones">
                  <YIcon.Dots size={16} />
                </YIconBtn>
              </div>
            </div>

            <YTabs
              value={tab}
              onChange={setTab}
              tabs={[
                { id: 'info', label: 'Información' },
                { id: 'polizas', label: 'Polizas' },
              ]}
            />

            {tab === 'info' && (
              <>
                {selected ? (
                  <PersonaForm
                    persona={selected}
                    onSave={handleSave}
                    saving={saving}
                    dirty={dirty}
                    setDirty={setDirty}
                  />
                ) : (
                  <div className="y-form-body">
                    <p style={{ fontSize: 13, color: 'var(--yool-muted)' }}>Cargando...</p>
                  </div>
                )}
                <div className="y-form-footer">
                  <div className="y-form-footer-meta">
                    {dirty
                      ? <>Tienes <span style={{ color: 'var(--fy-orange)', fontWeight: 700 }}>cambios sin guardar</span>.</>
                      : <>Cambios sincronizados</>}
                  </div>
                  <div className="y-form-footer-actions">
                    <YBtn variant="quiet" disabled={!dirty} onClick={() => setDirty(false)}>Descartar</YBtn>
                    <YBtn
                      variant="primary"
                      disabled={!dirty || saving}
                      onClick={() => document.querySelector('.y-detail-card form')?.requestSubmit()}
                    >
                      {saving ? 'Guardando...' : 'Guardar cambios'}
                    </YBtn>
                  </div>
                </div>
              </>
            )}

            {tab === 'polizas' && <PolizasTab personaId={selectedPersona.id} supabaseClient={supabaseClient} />}
          </div>
        )}
      </div>
    </div>
  );
}
